import { useMemo, useState } from 'react';
import { List, Radio, Typography } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { CustomNotification, dialogFunction } from 'common/components';
import { useSubmitWizardForm } from './useSubmitWizardForm';
import { useGetWizardPaymentInfo } from './useGetWizardPaymentInfo';
import {
  applyValidationResult,
  getConfigConfirmationItems,
} from '../utils/validation';
import {
  transformWizardItems,
  getQuestionParams,
  totalCostWizardProducts,
  autoPopulateCaseGtin,
} from '../utils/wizardProduct';
import { MODAL_TYPES } from '../config/stepModalConstants';
import { useSaveWizardForm } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useSaveWizardForm';

// Case/Innerpack fields are what the WarningWithConfirmation message is
// actually about, so surface whichever ones the product has populated —
// one per line.
const getCasePackDetails = (item) => {
  const parts = [];
  if (item.unitUpc) parts.push(`Unit UPC: ${item.unitUpc}`);
  if (item.caseUpc12) parts.push(`Case UPC-12: ${item.caseUpc12}`);
  if (item.caseGtin) parts.push(`Case GTIN: ${item.caseGtin}`);
  if (item.innerPackUpc) parts.push(`Innerpack UPC: ${item.innerPackUpc}`);
  if (
    item.innerPackPackSize !== null &&
    item.innerPackPackSize !== undefined &&
    item.innerPackPackSize !== ''
  )
    parts.push(`Innerpack Pack Size: ${item.innerPackPackSize}`);
  if (item.caseInnerPackCount)
    parts.push(`Case Innerpack Count: ${item.caseInnerPackCount}`);
  if (item.casePackSize) parts.push(`Case Pack Size: ${item.casePackSize}`);
  return parts;
};

// Per-product Yes/No list rendered inside the WarningWithConfirmation
// dialogFunction content. Selection is reported to the caller via onChange
// so the Promise wrapping the dialog can read the latest accepted ids when
// OK is clicked. Defaults every product to "Yes" (new configuration).
const ConfigConfirmationList = ({ items, onChange }) => {
  const [acceptedIds, setAcceptedIds] = useState(
    items.map((item) => item.uniqueId)
  );

  const setItemAccepted = (uniqueId, accepted) => {
    const next = accepted
      ? [...acceptedIds, uniqueId]
      : acceptedIds.filter((id) => id !== uniqueId);
    setAcceptedIds(next);
    onChange(next);
  };

  return (
    <>
      <Typography.Text
        style={{ display: 'block', marginBottom: 12, textAlign: 'left' }}
      >
        Please review the message for each product below and confirm whether to
        proceed.
      </Typography.Text>

      <List
        split={false}
        dataSource={items}
        style={{ maxHeight: 400, textAlign: 'left' }}
        className='scroller'
        renderItem={(item, index) => {
          const casePackDetails = getCasePackDetails(item);

          return (
            <List.Item style={{ padding: '4px 4px', display: 'block' }}>
              <div style={{ width: '100%', textAlign: 'left' }}>
                <Typography.Text
                  strong
                  style={{ display: 'block', fontSize: 14 }}
                  ellipsis={{ tooltip: true }}
                >
                  {item.productName || item.productDescription}
                </Typography.Text>

                {casePackDetails.map((line) => (
                  <Typography.Text
                    key={line}
                    style={{ display: 'block', fontSize: 13 }}
                    ellipsis={{ tooltip: true }}
                  >
                    {line}
                  </Typography.Text>
                ))}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 16,
                    marginTop: 8,
                    padding: '6px 10px',
                    background: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: 4,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 6,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <ExclamationCircleFilled
                      style={{ color: '#faad14', marginTop: 3 }}
                    />
                    <Typography.Text style={{ textAlign: 'left' }}>
                      {item.message}
                    </Typography.Text>
                  </div>

                  <Radio.Group
                    value={acceptedIds.includes(item.uniqueId)}
                    onChange={(e) =>
                      setItemAccepted(item.uniqueId, e.target.value)
                    }
                  >
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </div>
              </div>

              {index < items.length - 1 && (
                <div
                  style={{
                    width: '80%',
                    height: 1,
                    background: '#f0f0f0',
                    margin: '12px 0 0',
                  }}
                />
              )}
            </List.Item>
          );
        }}
      />
    </>
  );
};

export const useWizardSubmission = ({
  state,
  actions,
  openModal,
  closeModal,
  memberId,
  handleValidate,
  onZeroCostSuccess,
  gridRef,
} = {}) => {
  const { isSubmitting, submitWizardFormAsync } = useSubmitWizardForm();
  const { data: wizardPaymentInfo, fetchPaymentInfo } = useGetWizardPaymentInfo(
    { wizardFormId: state?.wizardFormId },
    { enabled: false }
  );

  const { saveWizardFormAsync } = useSaveWizardForm();

  const passedProduct = useMemo(
    () => state.products.filter((p) => p.canSubmit !== false),
    [state.products]
  );

  const passedProductCount = passedProduct.length;

  const hasErrors = useMemo(
    () => state.products.some((p) => p.canSubmit === false),
    [state.products]
  );

  const handleSubmissionContinue = async (newWizardFormId, isZeroCost) => {
    const { products } = state;
    const wizardFormId = newWizardFormId || state.wizardFormId;

    let productsToSubmit = products;
    let isAllPassed = products.every((p) => Boolean(p.canSubmit));

    if (!isAllPassed) {
      const response = await handleValidate({
        products,
        memberId,
        showMessage: false,
      });
      productsToSubmit = applyValidationResult({ response, products });

      actions.setProducts(productsToSubmit);
      setTimeout(() => {
        gridRef?.current?.api?.refreshCells({ force: true });
      }, 0);
      isAllPassed = productsToSubmit.every((p) => Boolean(p.canSubmit));
    }

    actions.setGridLoading(true);
    try {
      if (isAllPassed) {
        const response = await submitWizardFormAsync({ wizardFormId });

        if (response?.isSuccess) {
          closeModal(MODAL_TYPES.CONFIRM_SUBMISSION);
          closeModal(MODAL_TYPES.ONLINE_GRID);
          if (isZeroCost) {
            openModal(MODAL_TYPES.ZERO_COST_SUCCESS);
            onZeroCostSuccess?.();
            return;
          }
          closeModal(MODAL_TYPES.DIRECT_ONLINE_GRID);
          openModal(MODAL_TYPES.ACCEPT_SUBMISSION);
        } else {
          CustomNotification.error(
            response?.message ?? 'Failed to submit form'
          );
        }
      } else {
        closeModal(MODAL_TYPES.CONFIRM_SUBMISSION);
        openModal(MODAL_TYPES.ACCEPT_SUBMISSION);
      }
    } catch (error) {
    } finally {
      actions.setGridLoading(false);
    }
  };

  const handleSaveAndPrepareSubmit = async ({ wizardFormId }) => {
    actions.setGridLoading(true);
    try {
      const products = transformWizardItems(state?.products).map(
        autoPopulateCaseGtin
      );

      // ── 1. Always validate via API first — client-side status may be stale ──
      const responseValidation = await handleValidate({
        products,
        memberId,
        showMessage: false,
      });

      const productsToSubmit = applyValidationResult({
        response: responseValidation,
        products,
      });

      // Update grid immediately so user sees the real status
      actions.setProducts(productsToSubmit);
      setTimeout(() => {
        gridRef?.current?.api?.refreshCells({ force: true });
      }, 0);

      // ── 2. Block if any product has errors ─────────────────────────────────
      const hasErrors = productsToSubmit.some((p) => p.canSubmit === false);

      if (hasErrors) {
        CustomNotification.error(
          'Validation issues found. Please review before submitting.'
        );
        return;
      }

      // ── 3. WarningWithConfirmation — per-product Yes/No, maps isNewConfiguration ──
      // These two dialogs are separate flows, not chained: once the user has
      // reviewed and clicked Proceed on the per-product confirmation, that
      // covers their intent to proceed — skip the blanket warnings dialog
      // entirely rather than asking again.
      const confirmationItems = getConfigConfirmationItems(productsToSubmit);

      let productsWithConfirmation = products;
      if (confirmationItems.length > 0) {
        const acceptedUniqueIds = await new Promise((resolve) => {
          let accepted = confirmationItems.map((item) => item.uniqueId);

          dialogFunction({
            type: 'warn',
            title: null,
            className: 'wizard-config-confirm-dialog',
            content: (
              <ConfigConfirmationList
                items={confirmationItems}
                onChange={(ids) => {
                  accepted = ids;
                }}
              />
            ),
            okText: 'Proceed',
            cancelText: 'Cancel',
            onOk: () => resolve(accepted),
            onCancel: () => resolve(null),
            zIndex: 1400,
            width: 640,
          });
        });

        if (acceptedUniqueIds === null) return; // user cancelled the dialog

        const confirmedUniqueIds = new Set(acceptedUniqueIds);
        productsWithConfirmation = products.map((product) =>
          confirmedUniqueIds.has(product.uniqueId)
            ? { ...product, isNewConfiguration: true }
            : product
        );
      } else {
        // ── 3.5 Warning — blanket confirm, let user proceed directly ──────────
        const hasWarnings = productsToSubmit.some(
          (p) => p.status === 'warning'
        );

        if (hasWarnings) {
          const confirmed = await new Promise((resolve) => {
            dialogFunction({
              type: 'warn',
              content: (
                <Typography.Text>
                  Some products have warnings. Do you still want to proceed with
                  submission?
                </Typography.Text>
              ),
              okText: 'Proceed',
              cancelText: 'Cancel',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
              zIndex: 1400,
              width: 440,
            });
          });

          if (!confirmed) return;
        }
      }

      // ── 4. Save and proceed to payment / submission ─────────────────────────
      const requestPayload = {
        ...getQuestionParams(state),
        memberId,
        wizardFormId: state.wizardFormId ?? wizardFormId,
        wizardFormItems: productsWithConfirmation,
      };

      const response = await saveWizardFormAsync(requestPayload);

      if (response?.isSuccess) {
        const newWizardFormId = response?.data?.wizardFormId;
        actions.setWizardFormId(newWizardFormId);

        const paymentResponse = await fetchPaymentInfo(newWizardFormId);
        const { isZeroCost } = totalCostWizardProducts(paymentResponse?.data);

        if (isZeroCost) {
          await handleSubmissionContinue(newWizardFormId, isZeroCost);
        } else {
          openModal(MODAL_TYPES.CONFIRM_SUBMISSION);
        }
      } else {
        CustomNotification.error(response?.message);
      }
    } finally {
      actions.setGridLoading(false);
    }
  };

  return {
    isSubmitting,
    wizardPaymentInfo,
    passedProductCount,
    passedProduct,
    hasErrors,
    handleSubmissionContinue,
    handleSaveAndPrepareSubmit,
  };
};
